/**
 * FikoSPHRender.ts
 *
 * Multi-mode WebGPU SPH fluid renderer
 * Supports particle rendering, screen-space fluid rendering, and heatmap overlays
 *
 * @author FikoYT
 * @version 1.0.0
 */

import {
  PARTICLE_VERTEX_SHADER,
  PARTICLE_FRAGMENT_SHADER
} from './sph-particle-render.wgsl';
import {
  FLUID_DEPTH_VERTEX,
  FLUID_DEPTH_FRAGMENT,
  FLUID_BLUR_COMPUTE,
  FLUID_NORMAL_COMPUTE,
  FLUID_COMPOSITE_VERTEX,
  FLUID_COMPOSITE_FRAGMENT
} from './sph-fluid-render.wgsl';
import { HEATMAP_COMPUTE } from './sph-heatmap-render.wgsl';

/**
 * Render mode enumeration
 */
export enum RenderModu {
  PARTICLE = 0,  // Point sprite / billboard sphere rendering
  FLUID = 1,     // Screen-space fluid rendering
  HIBRIT = 2     // Hybrid: fluid surface with particle overlay
}

/**
 * Render settings interface
 */
export interface RenderAyarlari {
  modu: RenderModu;
  particleBoyutu: number;         // 0.5 - 5.0, default 2.0
  renkModu: number;               // 0=solid, 1=velocity, 2=pressure, etc.
  blurKernelSize: number;         // 5-25, default 11 (must be odd)
  blurDepthFalloff: number;       // 0.1-10, default 2.0
  saydamlik: number;              // 0-1, default 0.8
  kirilmaIndisi: number;          // 1.0-2.0, default 1.33 (water)
  absorpsiyon: number;            // 0-5, default 1.0
  spekuler: number;               // 0-1, default 0.5
  heatmapAktif: boolean;          // Heatmap overlay active
  heatmapTipi: number;            // 0-7 (velocity, pressure, density, etc.)
  siviRenk: { r: number; g: number; b: number }; // Default blue
}

/**
 * Camera data structure
 */
export interface KameraData {
  view: Float32Array;      // 16 floats (4x4 matrix)
  proj: Float32Array;      // 16 floats (4x4 matrix)
  invProj: Float32Array;   // 16 floats (4x4 matrix)
  pos: [number, number, number]; // Camera world position
}

/**
 * Default render settings
 */
const DEFAULT_AYARLAR: RenderAyarlari = {
  modu: RenderModu.FLUID,
  particleBoyutu: 2.0,
  renkModu: 0,
  blurKernelSize: 11,
  blurDepthFalloff: 2.0,
  saydamlik: 0.8,
  kirilmaIndisi: 1.33,
  absorpsiyon: 1.0,
  spekuler: 0.5,
  heatmapAktif: false,
  heatmapTipi: 0,
  siviRenk: { r: 0.2, g: 0.5, b: 0.9 }
};

/**
 * FikoSPHRender - WebGPU SPH Fluid Renderer
 *
 * Manages all rendering pipelines and resources for SPH fluid visualization
 */
export class FikoSPHRender {
  // Core WebGPU resources
  private device!: GPUDevice;
  private context!: GPUCanvasContext;
  private format!: GPUTextureFormat;
  private canvasWidth: number = 0;
  private canvasHeight: number = 0;

  // Render pipelines
  private particlePipeline?: GPURenderPipeline;
  private depthPipeline?: GPURenderPipeline;
  private compositePipeline?: GPURenderPipeline;
  private blurPipeline?: GPUComputePipeline;
  private normalPipeline?: GPUComputePipeline;
  private heatmapPipeline?: GPUComputePipeline;

  // Textures for screen-space fluid rendering
  private depthTexture?: GPUTexture;
  private depthTextureView?: GPUTextureView;
  private blurredDepthTexture?: GPUTexture;
  private blurredDepthTextureView?: GPUTextureView;
  private normalTexture?: GPUTexture;
  private normalTextureView?: GPUTextureView;
  private sceneDepthTexture?: GPUTexture; // For depth testing
  private sceneDepthTextureView?: GPUTextureView;

  // Bind groups and layouts
  private particleBindGroup?: GPUBindGroup;
  private depthBindGroup?: GPUBindGroup;
  private blurBindGroup?: GPUBindGroup;
  private normalBindGroup?: GPUBindGroup;
  private compositeBindGroup?: GPUBindGroup;
  private heatmapBindGroup?: GPUBindGroup;

  // Uniform buffers
  private renderParamsBuffer?: GPUBuffer;

  // Settings
  private renderAyarlari: RenderAyarlari;

  // Temporary texture for ping-pong blur
  private tempBlurTexture?: GPUTexture;
  private tempBlurTextureView?: GPUTextureView;

  constructor() {
    this.renderAyarlari = { ...DEFAULT_AYARLAR };
  }

  /**
   * Initialize the renderer
   */
  async basla(device: GPUDevice, context: GPUCanvasContext, format: GPUTextureFormat): Promise<void> {
    this.device = device;
    this.context = context;
    this.format = format;

    // Get initial canvas dimensions
    const canvas = context.canvas as HTMLCanvasElement;
    this.canvasWidth = canvas.width;
    this.canvasHeight = canvas.height;

    // Create uniform buffer for render parameters
    this.createRenderParamsBuffer();

    // Create textures
    this.createTextures();

    // Create all pipelines
    await this.createParticlePipeline();
    await this.createDepthPipeline();
    await this.createBlurPipeline();
    await this.createNormalPipeline();
    await this.createCompositePipeline();
    await this.createHeatmapPipeline();
  }

  /**
   * Create render parameters uniform buffer
   */
  private createRenderParamsBuffer(): void {
    // Buffer layout (256 bytes total, aligned):
    // mat4x4 view (64)
    // mat4x4 proj (64)
    // mat4x4 invProj (64)
    // vec3 cameraPos (12) + padding (4)
    // vec3 fluidColor (12) + padding (4)
    // float particleSize (4)
    // float transparency (4)
    // float refractionIndex (4)
    // float absorption (4)
    // float specular (4)
    // float blurDepthFalloff (4)
    // int blurKernelSize (4)
    // int colorMode (4)
    // int heatmapType (4)
    // int heatmapActive (4)
    // vec2 resolution (8)
    // padding to 256

    this.renderParamsBuffer = this.device.createBuffer({
      size: 256,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      label: 'SPH Render Params Buffer'
    });
  }

  /**
   * Create all render textures
   */
  private createTextures(): void {
    const width = this.canvasWidth;
    const height = this.canvasHeight;

    // Depth texture for fluid rendering (float32)
    this.depthTexture = this.device.createTexture({
      size: { width, height },
      format: 'r32float',
      usage: GPUTextureUsage.RENDER_ATTACHMENT |
             GPUTextureUsage.TEXTURE_BINDING |
             GPUTextureUsage.STORAGE_BINDING,
      label: 'SPH Fluid Depth Texture'
    });
    this.depthTextureView = this.depthTexture.createView();

    // Blurred depth texture
    this.blurredDepthTexture = this.device.createTexture({
      size: { width, height },
      format: 'r32float',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING,
      label: 'SPH Blurred Depth Texture'
    });
    this.blurredDepthTextureView = this.blurredDepthTexture.createView();

    // Temporary blur texture for ping-pong
    this.tempBlurTexture = this.device.createTexture({
      size: { width, height },
      format: 'r32float',
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
      label: 'SPH Temp Blur Texture'
    });
    this.tempBlurTextureView = this.tempBlurTexture.createView();

    // Normal texture (rgba16float)
    this.normalTexture = this.device.createTexture({
      size: { width, height },
      format: 'rgba16float',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING,
      label: 'SPH Normal Texture'
    });
    this.normalTextureView = this.normalTexture.createView();

    // Scene depth texture for depth testing
    this.sceneDepthTexture = this.device.createTexture({
      size: { width, height },
      format: 'depth24plus',
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
      label: 'SPH Scene Depth Texture'
    });
    this.sceneDepthTextureView = this.sceneDepthTexture.createView();
  }

  /**
   * Create particle rendering pipeline
   */
  private async createParticlePipeline(): Promise<void> {
    const shaderModule = this.device.createShaderModule({
      code: PARTICLE_VERTEX_SHADER + '\n' + PARTICLE_FRAGMENT_SHADER,
      label: 'SPH Particle Shader'
    });

    this.particlePipeline = this.device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module: shaderModule,
        entryPoint: 'vertexMain',
        buffers: [
          {
            // Position buffer
            arrayStride: 12, // vec3<f32>
            stepMode: 'instance',
            attributes: [{
              shaderLocation: 0,
              offset: 0,
              format: 'float32x3'
            }]
          },
          {
            // Velocity buffer
            arrayStride: 12, // vec3<f32>
            stepMode: 'instance',
            attributes: [{
              shaderLocation: 1,
              offset: 0,
              format: 'float32x3'
            }]
          },
          {
            // Heatmap color buffer
            arrayStride: 16, // vec4<f32>
            stepMode: 'instance',
            attributes: [{
              shaderLocation: 2,
              offset: 0,
              format: 'float32x4'
            }]
          }
        ]
      },
      fragment: {
        module: shaderModule,
        entryPoint: 'fragmentMain',
        targets: [{
          format: this.format,
          blend: {
            color: {
              srcFactor: 'src-alpha',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add'
            },
            alpha: {
              srcFactor: 'one',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add'
            }
          }
        }]
      },
      primitive: {
        topology: 'triangle-list'
      },
      depthStencil: {
        format: 'depth24plus',
        depthWriteEnabled: true,
        depthCompare: 'less'
      },
      label: 'SPH Particle Pipeline'
    });
  }

  /**
   * Create depth rendering pipeline for fluid surface
   */
  private async createDepthPipeline(): Promise<void> {
    const shaderModule = this.device.createShaderModule({
      code: FLUID_DEPTH_VERTEX + '\n' + FLUID_DEPTH_FRAGMENT,
      label: 'SPH Fluid Depth Shader'
    });

    this.depthPipeline = this.device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module: shaderModule,
        entryPoint: 'vertexMain',
        buffers: [
          {
            // Position buffer
            arrayStride: 12,
            stepMode: 'instance',
            attributes: [{
              shaderLocation: 0,
              offset: 0,
              format: 'float32x3'
            }]
          }
        ]
      },
      fragment: {
        module: shaderModule,
        entryPoint: 'fragmentMain',
        targets: [{
          format: 'r32float'
        }]
      },
      primitive: {
        topology: 'triangle-list'
      },
      depthStencil: {
        format: 'depth24plus',
        depthWriteEnabled: true,
        depthCompare: 'less'
      },
      label: 'SPH Fluid Depth Pipeline'
    });
  }

  /**
   * Create blur compute pipeline
   */
  private async createBlurPipeline(): Promise<void> {
    const shaderModule = this.device.createShaderModule({
      code: FLUID_BLUR_COMPUTE,
      label: 'SPH Blur Compute Shader'
    });

    this.blurPipeline = this.device.createComputePipeline({
      layout: 'auto',
      compute: {
        module: shaderModule,
        entryPoint: 'main'
      },
      label: 'SPH Blur Compute Pipeline'
    });
  }

  /**
   * Create normal computation pipeline
   */
  private async createNormalPipeline(): Promise<void> {
    const shaderModule = this.device.createShaderModule({
      code: FLUID_NORMAL_COMPUTE,
      label: 'SPH Normal Compute Shader'
    });

    this.normalPipeline = this.device.createComputePipeline({
      layout: 'auto',
      compute: {
        module: shaderModule,
        entryPoint: 'main'
      },
      label: 'SPH Normal Compute Pipeline'
    });
  }

  /**
   * Create composite rendering pipeline
   */
  private async createCompositePipeline(): Promise<void> {
    const shaderModule = this.device.createShaderModule({
      code: FLUID_COMPOSITE_VERTEX + '\n' + FLUID_COMPOSITE_FRAGMENT,
      label: 'SPH Composite Shader'
    });

    this.compositePipeline = this.device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module: shaderModule,
        entryPoint: 'vertexMain'
      },
      fragment: {
        module: shaderModule,
        entryPoint: 'fragmentMain',
        targets: [{
          format: this.format,
          blend: {
            color: {
              srcFactor: 'src-alpha',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add'
            },
            alpha: {
              srcFactor: 'one',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add'
            }
          }
        }]
      },
      primitive: {
        topology: 'triangle-list'
      },
      label: 'SPH Composite Pipeline'
    });
  }

  /**
   * Create heatmap compute pipeline
   */
  private async createHeatmapPipeline(): Promise<void> {
    const shaderModule = this.device.createShaderModule({
      code: HEATMAP_COMPUTE,
      label: 'SPH Heatmap Compute Shader'
    });

    this.heatmapPipeline = this.device.createComputePipeline({
      layout: 'auto',
      compute: {
        module: shaderModule,
        entryPoint: 'main'
      },
      label: 'SPH Heatmap Compute Pipeline'
    });
  }

  /**
   * Handle canvas resize
   */
  boyutDegisti(width: number, height: number): void {
    if (width === this.canvasWidth && height === this.canvasHeight) {
      return;
    }

    this.canvasWidth = width;
    this.canvasHeight = height;

    // Destroy old textures
    this.depthTexture?.destroy();
    this.blurredDepthTexture?.destroy();
    this.tempBlurTexture?.destroy();
    this.normalTexture?.destroy();
    this.sceneDepthTexture?.destroy();

    // Recreate textures
    this.createTextures();

    // Invalidate bind groups (will be recreated on next render)
    this.depthBindGroup = undefined;
    this.blurBindGroup = undefined;
    this.normalBindGroup = undefined;
    this.compositeBindGroup = undefined;
  }

  /**
   * Update render parameters buffer
   */
  private updateRenderParams(kamera: KameraData): void {
    if (!this.renderParamsBuffer) return;

    const data = new ArrayBuffer(256);
    const view = new Float32Array(data);
    const intView = new Int32Array(data);

    let offset = 0;

    // Camera view matrix (16 floats)
    view.set(kamera.view, offset);
    offset += 16;

    // Camera projection matrix (16 floats)
    view.set(kamera.proj, offset);
    offset += 16;

    // Camera inverse projection matrix (16 floats)
    view.set(kamera.invProj, offset);
    offset += 16;

    // Camera position (3 floats + 1 padding)
    view[offset++] = kamera.pos[0];
    view[offset++] = kamera.pos[1];
    view[offset++] = kamera.pos[2];
    offset++; // padding

    // Fluid color (3 floats + 1 padding)
    view[offset++] = this.renderAyarlari.siviRenk.r;
    view[offset++] = this.renderAyarlari.siviRenk.g;
    view[offset++] = this.renderAyarlari.siviRenk.b;
    offset++; // padding

    // Render parameters
    view[offset++] = this.renderAyarlari.particleBoyutu;
    view[offset++] = this.renderAyarlari.saydamlik;
    view[offset++] = this.renderAyarlari.kirilmaIndisi;
    view[offset++] = this.renderAyarlari.absorpsiyon;
    view[offset++] = this.renderAyarlari.spekuler;
    view[offset++] = this.renderAyarlari.blurDepthFalloff;

    // Integer parameters
    const intOffset = offset;
    intView[intOffset] = this.renderAyarlari.blurKernelSize;
    intView[intOffset + 1] = this.renderAyarlari.renkModu;
    intView[intOffset + 2] = this.renderAyarlari.heatmapTipi;
    intView[intOffset + 3] = this.renderAyarlari.heatmapAktif ? 1 : 0;
    offset += 4;

    // Resolution (2 floats)
    view[offset++] = this.canvasWidth;
    view[offset++] = this.canvasHeight;

    // Write to GPU
    this.device.queue.writeBuffer(this.renderParamsBuffer, 0, data);
  }

  /**
   * Create or update bind groups
   */
  private updateBindGroups(
    positionBuffer: GPUBuffer,
    velocityBuffer: GPUBuffer,
    densityBuffer: GPUBuffer,
    heatmapBuffer: GPUBuffer
  ): void {
    if (!this.renderParamsBuffer) return;

    // Particle bind group
    if (!this.particleBindGroup && this.particlePipeline) {
      this.particleBindGroup = this.device.createBindGroup({
        layout: this.particlePipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.renderParamsBuffer } }
        ],
        label: 'SPH Particle Bind Group'
      });
    }

    // Depth bind group
    if (!this.depthBindGroup && this.depthPipeline) {
      this.depthBindGroup = this.device.createBindGroup({
        layout: this.depthPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.renderParamsBuffer } }
        ],
        label: 'SPH Depth Bind Group'
      });
    }

    // Blur bind group (ping-pong textures)
    if (!this.blurBindGroup && this.blurPipeline && this.depthTextureView &&
        this.blurredDepthTextureView && this.tempBlurTextureView) {
      this.blurBindGroup = this.device.createBindGroup({
        layout: this.blurPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.renderParamsBuffer } },
          { binding: 1, resource: this.depthTextureView },
          { binding: 2, resource: this.tempBlurTextureView },
          { binding: 3, resource: this.blurredDepthTextureView }
        ],
        label: 'SPH Blur Bind Group'
      });
    }

    // Normal bind group
    if (!this.normalBindGroup && this.normalPipeline && this.blurredDepthTextureView &&
        this.normalTextureView) {
      this.normalBindGroup = this.device.createBindGroup({
        layout: this.normalPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.renderParamsBuffer } },
          { binding: 1, resource: this.blurredDepthTextureView },
          { binding: 2, resource: this.normalTextureView }
        ],
        label: 'SPH Normal Bind Group'
      });
    }

    // Composite bind group
    if (!this.compositeBindGroup && this.compositePipeline && this.blurredDepthTextureView &&
        this.normalTextureView) {
      this.compositeBindGroup = this.device.createBindGroup({
        layout: this.compositePipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.renderParamsBuffer } },
          { binding: 1, resource: this.blurredDepthTextureView },
          { binding: 2, resource: this.normalTextureView }
        ],
        label: 'SPH Composite Bind Group'
      });
    }

    // Heatmap bind group
    if (!this.heatmapBindGroup && this.heatmapPipeline) {
      this.heatmapBindGroup = this.device.createBindGroup({
        layout: this.heatmapPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.renderParamsBuffer } },
          { binding: 1, resource: { buffer: velocityBuffer } },
          { binding: 2, resource: { buffer: densityBuffer } },
          { binding: 3, resource: { buffer: heatmapBuffer } }
        ],
        label: 'SPH Heatmap Bind Group'
      });
    }
  }

  /**
   * Render a frame
   */
  renderFrame(
    encoder: GPUCommandEncoder,
    outputView: GPUTextureView,
    kamera: KameraData,
    positionBuffer: GPUBuffer,
    velocityBuffer: GPUBuffer,
    densityBuffer: GPUBuffer,
    heatmapBuffer: GPUBuffer,
    activeCount: number
  ): void {
    if (activeCount === 0) return;

    // Update render parameters
    this.updateRenderParams(kamera);

    // Update bind groups
    this.updateBindGroups(positionBuffer, velocityBuffer, densityBuffer, heatmapBuffer);

    // Compute heatmap if active
    if (this.renderAyarlari.heatmapAktif && this.heatmapPipeline && this.heatmapBindGroup) {
      const computePass = encoder.beginComputePass({ label: 'SPH Heatmap Pass' });
      computePass.setPipeline(this.heatmapPipeline);
      computePass.setBindGroup(0, this.heatmapBindGroup);
      computePass.dispatchWorkgroups(Math.ceil(activeCount / 64));
      computePass.end();
    }

    // Render based on mode
    switch (this.renderAyarlari.modu) {
      case RenderModu.PARTICLE:
        this.renderParticleMode(encoder, outputView, positionBuffer, velocityBuffer,
                               heatmapBuffer, activeCount);
        break;

      case RenderModu.FLUID:
        this.renderFluidMode(encoder, outputView, positionBuffer, activeCount);
        break;

      case RenderModu.HIBRIT:
        this.renderHibritMode(encoder, outputView, positionBuffer, velocityBuffer,
                             heatmapBuffer, activeCount);
        break;
    }
  }

  /**
   * Render particle mode
   */
  private renderParticleMode(
    encoder: GPUCommandEncoder,
    outputView: GPUTextureView,
    positionBuffer: GPUBuffer,
    velocityBuffer: GPUBuffer,
    heatmapBuffer: GPUBuffer,
    activeCount: number
  ): void {
    if (!this.particlePipeline || !this.particleBindGroup || !this.sceneDepthTextureView) return;

    const renderPass = encoder.beginRenderPass({
      label: 'SPH Particle Render Pass',
      colorAttachments: [{
        view: outputView,
        loadOp: 'load',
        storeOp: 'store'
      }],
      depthStencilAttachment: {
        view: this.sceneDepthTextureView,
        depthLoadOp: 'clear',
        depthStoreOp: 'store',
        depthClearValue: 1.0
      }
    });

    renderPass.setPipeline(this.particlePipeline);
    renderPass.setBindGroup(0, this.particleBindGroup);
    renderPass.setVertexBuffer(0, positionBuffer);
    renderPass.setVertexBuffer(1, velocityBuffer);
    renderPass.setVertexBuffer(2, heatmapBuffer);
    renderPass.draw(6, activeCount); // 2 triangles per particle billboard
    renderPass.end();
  }

  /**
   * Render fluid mode (screen-space fluid)
   */
  private renderFluidMode(
    encoder: GPUCommandEncoder,
    outputView: GPUTextureView,
    positionBuffer: GPUBuffer,
    activeCount: number
  ): void {
    if (!this.depthPipeline || !this.depthBindGroup || !this.depthTextureView ||
        !this.sceneDepthTextureView) return;

    // 1. Depth pass - render particle depths
    const depthPass = encoder.beginRenderPass({
      label: 'SPH Fluid Depth Pass',
      colorAttachments: [{
        view: this.depthTextureView,
        loadOp: 'clear',
        storeOp: 'store',
        clearValue: { r: 1e10, g: 0, b: 0, a: 1 }
      }],
      depthStencilAttachment: {
        view: this.sceneDepthTextureView,
        depthLoadOp: 'clear',
        depthStoreOp: 'store',
        depthClearValue: 1.0
      }
    });

    depthPass.setPipeline(this.depthPipeline);
    depthPass.setBindGroup(0, this.depthBindGroup);
    depthPass.setVertexBuffer(0, positionBuffer);
    depthPass.draw(6, activeCount);
    depthPass.end();

    // 2. Blur pass - bilateral blur on depth (2-pass separable)
    if (this.blurPipeline && this.blurBindGroup) {
      const blurPass = encoder.beginComputePass({ label: 'SPH Fluid Blur Pass' });
      blurPass.setPipeline(this.blurPipeline);
      blurPass.setBindGroup(0, this.blurBindGroup);

      // Horizontal pass
      blurPass.dispatchWorkgroups(
        Math.ceil(this.canvasWidth / 16),
        Math.ceil(this.canvasHeight / 16)
      );

      blurPass.end();
    }

    // 3. Normal pass - compute normals from blurred depth
    if (this.normalPipeline && this.normalBindGroup) {
      const normalPass = encoder.beginComputePass({ label: 'SPH Fluid Normal Pass' });
      normalPass.setPipeline(this.normalPipeline);
      normalPass.setBindGroup(0, this.normalBindGroup);
      normalPass.dispatchWorkgroups(
        Math.ceil(this.canvasWidth / 16),
        Math.ceil(this.canvasHeight / 16)
      );
      normalPass.end();
    }

    // 4. Composite pass - final shading
    if (this.compositePipeline && this.compositeBindGroup) {
      const compositePass = encoder.beginRenderPass({
        label: 'SPH Fluid Composite Pass',
        colorAttachments: [{
          view: outputView,
          loadOp: 'load',
          storeOp: 'store'
        }]
      });

      compositePass.setPipeline(this.compositePipeline);
      compositePass.setBindGroup(0, this.compositeBindGroup);
      compositePass.draw(3); // Fullscreen triangle
      compositePass.end();
    }
  }

  /**
   * Render hybrid mode (fluid + particle overlay)
   */
  private renderHibritMode(
    encoder: GPUCommandEncoder,
    outputView: GPUTextureView,
    positionBuffer: GPUBuffer,
    velocityBuffer: GPUBuffer,
    heatmapBuffer: GPUBuffer,
    activeCount: number
  ): void {
    // First render fluid surface
    this.renderFluidMode(encoder, outputView, positionBuffer, activeCount);

    // Then overlay particles with reduced opacity
    if (this.particlePipeline && this.particleBindGroup && this.sceneDepthTextureView) {
      const particlePass = encoder.beginRenderPass({
        label: 'SPH Hybrid Particle Overlay Pass',
        colorAttachments: [{
          view: outputView,
          loadOp: 'load',
          storeOp: 'store'
        }],
        depthStencilAttachment: {
          view: this.sceneDepthTextureView,
          depthLoadOp: 'load',
          depthStoreOp: 'store'
        }
      });

      particlePass.setPipeline(this.particlePipeline);
      particlePass.setBindGroup(0, this.particleBindGroup);
      particlePass.setVertexBuffer(0, positionBuffer);
      particlePass.setVertexBuffer(1, velocityBuffer);
      particlePass.setVertexBuffer(2, heatmapBuffer);
      particlePass.draw(6, activeCount);
      particlePass.end();
    }
  }

  /**
   * Update render settings
   */
  guncelleAyarlar(ayarlar: Partial<RenderAyarlari>): void {
    this.renderAyarlari = {
      ...this.renderAyarlari,
      ...ayarlar
    };

    // Validate blur kernel size (must be odd)
    if (ayarlar.blurKernelSize !== undefined) {
      if (this.renderAyarlari.blurKernelSize % 2 === 0) {
        this.renderAyarlari.blurKernelSize++;
      }
      this.renderAyarlari.blurKernelSize = Math.max(5, Math.min(25, this.renderAyarlari.blurKernelSize));
    }
  }

  /**
   * Get current render settings
   */
  ayarlariAl(): Readonly<RenderAyarlari> {
    return { ...this.renderAyarlari };
  }

  /**
   * Clean up all GPU resources
   */
  temizle(): void {
    // Destroy textures
    this.depthTexture?.destroy();
    this.blurredDepthTexture?.destroy();
    this.tempBlurTexture?.destroy();
    this.normalTexture?.destroy();
    this.sceneDepthTexture?.destroy();

    // Destroy buffers
    this.renderParamsBuffer?.destroy();

    // Clear references
    this.particlePipeline = undefined;
    this.depthPipeline = undefined;
    this.compositePipeline = undefined;
    this.blurPipeline = undefined;
    this.normalPipeline = undefined;
    this.heatmapPipeline = undefined;

    this.particleBindGroup = undefined;
    this.depthBindGroup = undefined;
    this.blurBindGroup = undefined;
    this.normalBindGroup = undefined;
    this.compositeBindGroup = undefined;
    this.heatmapBindGroup = undefined;

    this.depthTexture = undefined;
    this.depthTextureView = undefined;
    this.blurredDepthTexture = undefined;
    this.blurredDepthTextureView = undefined;
    this.tempBlurTexture = undefined;
    this.tempBlurTextureView = undefined;
    this.normalTexture = undefined;
    this.normalTextureView = undefined;
    this.sceneDepthTexture = undefined;
    this.sceneDepthTextureView = undefined;

    this.renderParamsBuffer = undefined;
  }
}
